import type { SWRConfiguration } from 'swr';

import { ApiError } from '@/utils/fetch.client';

/**
 * Whether a failed request is worth trying again.
 *
 * SWR's stock handler is status-blind — it backs off and retries a 401 exactly like a
 * 503. Most 4xx will never fix themselves, so retrying just delays the inevitable.
 *
 * 408 and 429 are the exceptions, and they matter: they mean "try again", and they are
 * precisely the two the *server* hands out under load (the REST router's 30s
 * TimeoutLayer, and the per-IP governor). Treating them like a 401 would turn a
 * momentary throttle into a permanent failure — and because the governor keys on IP, a
 * busy office can trip it without any one person doing anything wrong.
 */
export function isRetryableError(error: unknown): boolean {
  if (!(error instanceof ApiError)) return true;
  if (error.status === 408 || error.status === 429) return true;
  return !(error.status >= 400 && error.status < 500);
}

/**
 * SWR's default backoff, minus the 4xx it should never have retried.
 *
 * Trade-off of replacing the default: we lose its built-in "don't retry while offline or
 * while the document is hidden" checks.
 */
export const onErrorRetry: NonNullable<SWRConfiguration['onErrorRetry']> = (
  error,
  _key,
  config,
  revalidate,
  { retryCount },
) => {
  if (!isRetryableError(error)) return;
  if (retryCount > (config.errorRetryCount ?? 3)) return;

  const interval = config.errorRetryInterval ?? 5000;
  const timeout = ~~((Math.random() + 0.5) * (1 << Math.min(retryCount, 8))) * interval;
  setTimeout(revalidate, timeout, { retryCount });
};
